import React, { useRef } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress } from "@material-ui/core";
import { primary, darkBlue, red } from "../../styles/colors";
import { formatDate } from "../DuePayments/duePaymentsService";
import { useLoadMoreObserver } from "./useLoadMoreObserver";

const HEADERS = [
  { key: "po_number", label: "PO Number" },
  { key: "so_number", label: "SO Number" },
  { key: "so_quantity", label: "SO Qty", align: "right" },
  { key: "po_quantity", label: "PO Qty", align: "right" },
  { key: "cargo_received_quantity", label: "Cargo Received", align: "right" },
  { key: "balance_to_purchase", label: "Balance to Purchase", align: "right" },
  { key: "status", label: "Status", align: "center" },
  { key: "miller", label: "Miller" },
  { key: "broker", label: "Broker" },
  { key: "contract_date", label: "Contract Date", align: "center" },
];

const statusColors = {
  OPEN: { bg: "rgba(241, 154, 48, 0.15)", color: primary },
  CLOSED: { bg: "#d4edda", color: "#155724" },
  CANCELLED: { bg: "#f8d7da", color: "#721c24" },
};

const formatQty = (value, unit) => {
  if (value === null || value === undefined) return "—";
  const formatted = Number(value).toLocaleString("en-IN");
  return unit ? `${formatted} ${unit}` : formatted;
};

const QuantityTable = ({ rows = [], hasMore, loadingMore, onLoadMore }) => {
  const containerRef = useRef(null);
  const loadMoreTriggerRef = useLoadMoreObserver(containerRef, hasMore, loadingMore, onLoadMore);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      ref={containerRef}
      style={{
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        maxHeight: "calc(90vh - 260px)",
        overflow: "auto",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow style={{ backgroundColor: "#f8f9fa" }}>
            {HEADERS.map((h) => (
              <TableCell
                key={h.key}
                align={h.align || "left"}
                style={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#495057",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  padding: "12px",
                  borderBottom: "2px solid #e9ecef",
                  whiteSpace: "nowrap",
                }}
              >
                {h.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={HEADERS.length} align="center" style={{ padding: "40px", color: "#6c757d" }}>
                No purchase orders found
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const statusConfig = statusColors[row.status] || { bg: "#e8e8e8", color: "#495057" };
              const balanceNegative = Number(row.balance_to_purchase) < 0;
              return (
                <TableRow key={row.id} hover>
                  <TableCell style={{ padding: "10px 12px", fontWeight: 500, color: darkBlue }}>
                    {row.po_number || "—"}
                  </TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.so_number || "—"}</TableCell>
                  <TableCell align="right" style={{ padding: "10px 12px" }}>
                    {formatQty(row.so_quantity, row.so_unit)}
                  </TableCell>
                  <TableCell align="right" style={{ padding: "10px 12px" }}>
                    {formatQty(row.po_quantity, row.po_unit)}
                  </TableCell>
                  <TableCell align="right" style={{ padding: "10px 12px" }}>
                    {formatQty(row.cargo_received_quantity)}
                  </TableCell>
                  <TableCell
                    align="right"
                    style={{ padding: "10px 12px", fontWeight: 600, color: balanceNegative ? red : darkBlue }}
                  >
                    {formatQty(row.balance_to_purchase)}
                  </TableCell>
                  <TableCell align="center" style={{ padding: "10px 12px" }}>
                    <Chip
                      label={row.status || "N/A"}
                      size="small"
                      style={{
                        backgroundColor: statusConfig.bg,
                        color: statusConfig.color,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        height: "22px",
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.miller || "—"}</TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.broker || "—"}</TableCell>
                  <TableCell align="center" style={{ padding: "10px 12px" }}>
                    {formatDate(row.contract_date)}
                  </TableCell>
                </TableRow>
              );
            })
          )}
          {hasMore && (
            <TableRow>
              <TableCell colSpan={HEADERS.length} align="center" style={{ padding: "16px" }}>
                <div
                  ref={loadMoreTriggerRef}
                  style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", color: "#6c757d" }}
                >
                  {loadingMore && <CircularProgress size={18} style={{ color: primary }} />}
                  <span style={{ fontSize: "0.8rem" }}>{loadingMore ? "Loading more..." : "Scroll for more"}</span>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default QuantityTable;
